import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaMinus, FaPlus, FaTrash, FaShoppingCart, FaListUl } from 'react-icons/fa';
import toast from 'react-hot-toast';
import {
    get_my_list,
    update_my_list_quantity,
    remove_from_my_list,
    bulk_add_my_list_to_card,
    messageClear
} from '../../store/reducers/myListReducer';
import { useCurrency } from '../../context/CurrencyContext';

const MyList = () => {
    const dispatch = useDispatch();
    const { userInfo } = useSelector(s => s.auth);
    const { myList, successMessage, errorMessage, loading } = useSelector(s => s.myList);
    const { formatBase } = useCurrency();

    const [selectedItems, setSelectedItems] = useState([]);

    useEffect(() => {
        if (userInfo?.id) {
            dispatch(get_my_list(userInfo.id));
        }
    }, [dispatch, userInfo?.id]);

    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage);
            dispatch(messageClear());
        }
    }, [successMessage]);

    useEffect(() => {
        if (errorMessage) {
            toast.error(errorMessage);
            dispatch(messageClear());
        }
    }, [errorMessage]);

    const availableItems = myList.filter(i => i.available && i.product);
    const allIds = availableItems.map(i => i._id);

    const toggleSelect = (id) => {
        setSelectedItems(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        setSelectedItems(prev => prev.length === allIds.length ? [] : allIds);
    };

    const changeQty = (item, delta) => {
        const stock = item.product?.stock ?? Infinity;
        const nextQty = item.quantity + delta;
        if (nextQty < 1) return;
        if (stock && nextQty > stock) {
            toast.error('Only ' + stock + ' in stock');
            return;
        }
        dispatch(update_my_list_quantity({ myListId: item._id, quantity: nextQty }));
    };

    const handleRemove = (myListId) => {
        dispatch(remove_from_my_list(myListId));
        setSelectedItems(prev => prev.filter(i => i !== myListId));
    };

    const handleBulkAdd = () => {
        if (selectedItems.length === 0) {
            return toast.error('Select at least one product');
        }
        dispatch(bulk_add_my_list_to_card({ userId: userInfo.id, myListIds: selectedItems }));
    };

    return (
        <div className="space-y-3">
            {/* Header card */}
            <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center justify-between">
                <div>
                    <span className="text-sm font-semibold text-gray-800">My List</span>
                    <p className="text-xs text-gray-400">Your regular purchases</p>
                </div>
                <span className="text-xs text-gray-500">
                    {myList.length} item{myList.length !== 1 ? 's' : ''}
                </span>
            </div>

            {!loading && myList.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center justify-between flex-wrap gap-3">
                    <button
                        onClick={toggleSelectAll}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-700"
                    >
                        <span
                            className={`w-5 h-5 rounded flex items-center justify-center border transition
                            ${selectedItems.length === allIds.length && allIds.length > 0
                                ? 'bg-orange-500 border-orange-500 text-white'
                                : 'border-gray-300 text-transparent'}`}
                        >
                            ✓
                        </span>
                        Select All
                    </button>

                    <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">
                            {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
                        </span>
                        <button
                            onClick={handleBulkAdd}
                            disabled={selectedItems.length === 0}
                            className="flex items-center gap-2 px-5 py-2 rounded-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold transition-colors"
                        >
                            <FaShoppingCart className="text-[11px]" />
                            Add Selected to Cart
                        </button>
                    </div>
                </div>
            )}

            {myList.length > 0 ? (
                <div className="flex flex-col gap-3">
                    {myList.map((item) => {
                        const product = item.product;
                        const outOfStock = !product || product.stock < 1;
                        const finalPrice = product
                            ? product.price - Math.floor((product.price * (product.discount || 0)) / 100)
                            : 0;

                        return (
                            <div
                                key={item._id}
                                className="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-4 flex-wrap sm:flex-nowrap"
                            >
                                {/* Checkbox */}
                                <button
                                    onClick={() => product && !outOfStock && toggleSelect(item._id)}
                                    disabled={!product || outOfStock}
                                    className={`w-5 h-5 rounded flex items-center justify-center border shrink-0 transition
                                    ${selectedItems.includes(item._id)
                                        ? 'bg-orange-500 border-orange-500 text-white'
                                        : 'border-gray-300 text-transparent'}
                                    ${(!product || outOfStock) ? 'opacity-40 cursor-not-allowed' : ''}`}
                                >
                                    ✓
                                </button>

                                {/* Image */}
                                <div className="w-16 h-16 rounded-lg bg-gray-50 overflow-hidden shrink-0">
                                    {product ? (
                                        <img
                                            src={product.images?.[0]}
                                            alt={product.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
                                            N/A
                                        </div>
                                    )}
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-[140px]">
                                    {product ? (
                                        <Link
                                            to={`/product/details/${product.slug}`}
                                            className="text-[13px] font-medium text-gray-800 hover:text-orange-500 line-clamp-2"
                                        >
                                            {product.name}
                                        </Link>
                                    ) : (
                                        <span className="text-[13px] text-gray-400 italic">
                                            This product is no longer available
                                        </span>
                                    )}

                                    {product && (
                                        <div className="mt-1 flex items-center gap-2">
                                            <span className="text-sm font-bold text-gray-900">
                                                {formatBase(finalPrice)}
                                            </span>
                                            {product.discount > 0 && (
                                                <span className="text-[11px] text-gray-400 line-through">
                                                    {formatBase(product.price)}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {outOfStock && product && (
                                        <span className="inline-block mt-1 text-[11px] font-semibold text-red-500">
                                            Out of stock
                                        </span>
                                    )}
                                </div>

                                {/* Qty controls */}
                                {product && (
                                    <div className="flex items-center border border-gray-200 rounded-full overflow-hidden shrink-0">
                                        <button
                                            onClick={() => changeQty(item, -1)}
                                            disabled={item.quantity <= 1}
                                            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30"
                                        >
                                            <FaMinus size={10} />
                                        </button>
                                        <span className="w-8 text-center text-sm font-semibold">
                                            {item.quantity}
                                        </span>
                                        <button
                                            onClick={() => changeQty(item, 1)}
                                            disabled={outOfStock || (product.stock && item.quantity >= product.stock)}
                                            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30"
                                        >
                                            <FaPlus size={10} />
                                        </button>
                                    </div>
                                )}

                                {/* Remove */}
                                <button
                                    onClick={() => handleRemove(item._id)}
                                    className="w-8 h-8 rounded-full flex items-center justify-center border border-gray-200 hover:border-red-200 hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                                >
                                    <FaTrash size={12} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            ) : !loading && (
                <div className="bg-white rounded-lg border border-gray-200 flex flex-col items-center py-16">
                    <div className="w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center mb-3">
                        <FaListUl className="text-orange-400 text-xl" />
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Your My List is empty</p>
                    <p className="text-xs text-gray-400 mt-1 mb-5 text-center max-w-xs">
                        Products you purchase or add manually will show up here, ready for your next order.
                    </p>
                    <Link
                        to="/products"
                        className="px-6 py-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold transition-colors"
                    >
                        Start Shopping
                    </Link>
                </div>
            )}
        </div>
    );
};

export default MyList;
